/* eslint-disable space-before-function-paren */
/* eslint-disable no-useless-escape */
/**
 * JintValidator - Client-side JavaScript validation for Jint runtime compatibility
 *
 * Validates JavaScript code against Jint 4.4.1 constraints before server execution.
 * Catches blocked patterns, ES6+ features, and provides actionable suggestions.
 *
 * Usage:
 *   const validator = new JintValidator()
 *   const result = validator.validate(code)
 *   // result.markers can be applied to Monaco editor
 */

import {
	BLOCKED_PATTERNS,
	ES6_FEATURES,
	SUSPICIOUS_PATTERNS,
	JINT_LIMITS,
	MarkerSeverity,
	createMarker
} from './jint-patterns.js'

/**
 * Validates JavaScript code against Jint runtime constraints
 */
export class JintValidator {
	/**
	 * Validate JavaScript code against Jint constraints
	 * @param {string} code - JavaScript source code to validate
	 * @returns {Object} Validation result with markers and detected issues
	 */
	validate(code) {
		const markers = []
		const blockedPatterns = []
		const es6Features = []
		const suspiciousPatterns = []

		// Skip validation for empty code
		if (!code || !code.trim()) {
			return {
				markers,
				hasErrors: false,
				hasWarnings: false,
				blockedPatterns,
				es6Features,
				suspiciousPatterns
			}
		}

		// Run all validators
		this._validateBlockedPatterns(code, markers, blockedPatterns)
		this._validateES6Features(code, markers, es6Features)
		this._validateSuspiciousPatterns(code, markers, suspiciousPatterns)
		this._validateResourceLimits(code, markers)

		const hasErrors = markers.some(m => m.severity === MarkerSeverity.Error)
		const hasWarnings = markers.some(m => m.severity === MarkerSeverity.Warning)

		return {
			markers,
			hasErrors,
			hasWarnings,
			blockedPatterns,
			es6Features,
			suspiciousPatterns
		}
	}

	/**
	 * Validate code against blocked patterns (hard errors)
	 * @private
	 */
	_validateBlockedPatterns(code, markers, blockedList) {
		for (const patternDef of BLOCKED_PATTERNS) {
			const matches = this._findAllMatches(code, patternDef.pattern)

			for (const match of matches) {
				// Skip if inside a string literal or comment
				if (this._isInsideStringOrComment(code, match.index)) {
					continue
				}

				markers.push(createMarker(
					MarkerSeverity.Error,
					`Blocked: ${patternDef.message}`,
					match.line,
					match.column,
					match.line,
					match.column + match.length
				))

				if (!blockedList.includes(patternDef.keyword)) {
					blockedList.push(patternDef.keyword)
				}
			}
		}
	}

	/**
	 * Validate code for ES6+ features (warnings with suggestions)
	 * @private
	 */
	_validateES6Features(code, markers, featureList) {
		for (const featureDef of ES6_FEATURES) {
			const matches = this._findAllMatches(code, featureDef.pattern)

			for (const match of matches) {
				// Skip if inside a string literal or comment
				if (this._isInsideStringOrComment(code, match.index)) {
					continue
				}

				markers.push(createMarker(
					MarkerSeverity.Warning,
					`ES6+ Feature: ${featureDef.message}\nSuggestion: ${featureDef.suggestion}`,
					match.line,
					match.column,
					match.line,
					match.column + match.length
				))

				if (!featureList.includes(featureDef.keyword)) {
					featureList.push(featureDef.keyword)
				}
			}
		}
	}

	/**
	 * Validate code for suspicious patterns (info warnings)
	 * @private
	 */
	_validateSuspiciousPatterns(code, markers, suspiciousList) {
		for (const patternDef of SUSPICIOUS_PATTERNS) {
			const matches = this._findAllMatches(code, patternDef.pattern)

			for (const match of matches) {
				// Skip if inside a string literal or comment
				if (this._isInsideStringOrComment(code, match.index)) {
					continue
				}

				markers.push(createMarker(
					MarkerSeverity.Info,
					`Review: ${patternDef.message}`,
					match.line,
					match.column,
					match.line,
					match.column + match.length
				))

				if (!suspiciousList.includes(patternDef.keyword)) {
					suspiciousList.push(patternDef.keyword)
				}
			}
		}
	}

	/**
	 * Validate code for potential resource limit issues
	 * @private
	 */
	_validateResourceLimits(code, markers) {
		// Check for large loop literals
		this._checkLargeLoops(code, markers)

		// Check for deeply nested loops
		this._checkNestedLoops(code, markers)

		// Check for recursive function calls
		this._checkRecursiveFunctions(code, markers)

		// Check for string concatenation in loops (performance)
		this._checkStringConcatInLoops(code, markers)
	}

	/**
	 * Check for loops with large iteration counts
	 * @private
	 */
	_checkLargeLoops(code, markers) {
		// Match for loops with numeric limits: for (var i = 0; i < 5000; i++)
		const largeLoopPattern = /for\s*\([^;]*;\s*\w+\s*<\s*(\d+)/g
		let match

		while ((match = largeLoopPattern.exec(code)) !== null) {
			const limit = parseInt(match[1], 10)

			if (limit > 1000) {
				const position = this._getPositionFromIndex(code, match.index)

				markers.push(createMarker(
					MarkerSeverity.Info,
					`Large loop detected (${limit.toLocaleString()} iterations). Total loop limit is ${JINT_LIMITS.maxLoopIterations.toLocaleString()} iterations across all loops.`,
					position.line,
					position.column,
					position.line,
					position.column + match[0].length
				))
			}
		}
	}

	/**
	 * Check for deeply nested loops (3+ levels)
	 * @private
	 */
	_checkNestedLoops(code, markers) {
		const lines = code.split('\n')
		let nestingDepth = 0
		let maxNestingLine = -1
		let maxNestingColumn = -1

		for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
			const line = lines[lineIndex]

			// Count loop keywords
			const loopMatches = line.matchAll(/\b(for|while|do)\s*[\(\{]/g)

			for (const match of loopMatches) {
				nestingDepth++

				if (nestingDepth >= 3 && maxNestingLine === -1) {
					maxNestingLine = lineIndex + 1
					maxNestingColumn = (match.index || 0) + 1
				}
			}

			// Count closing braces (simplified - may not be perfectly accurate)
			const closingBraces = (line.match(/\}/g) || []).length
			nestingDepth = Math.max(0, nestingDepth - closingBraces)
		}

		if (maxNestingLine !== -1) {
			markers.push(createMarker(
				MarkerSeverity.Info,
				`Deeply nested loops detected (3+ levels). This may hit the iteration limit of ${JINT_LIMITS.maxLoopIterations.toLocaleString()} total iterations.`,
				maxNestingLine,
				maxNestingColumn,
				maxNestingLine,
				maxNestingColumn + 5
			))
		}
	}

	/**
	 * Check for recursive function calls
	 * @private
	 */
	_checkRecursiveFunctions(code, markers) {
		// Match function declarations and check if they call themselves
		const functionPattern = /function\s+(\w+)\s*\([^)]*\)\s*\{/g
		let match

		while ((match = functionPattern.exec(code)) !== null) {
			const functionName = match[1]
			const functionStart = match.index + match[0].length

			// Find the matching closing brace (simplified)
			let braceCount = 1
			let searchIndex = functionStart

			while (braceCount > 0 && searchIndex < code.length) {
				const char = code[searchIndex]

				if (char === '{') braceCount++
				if (char === '}') braceCount--

				searchIndex++
			}

			const functionBody = code.substring(functionStart, searchIndex - 1)

			// Check if function calls itself
			// Escape special regex characters in function name
			const escapedName = functionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
			// Check if function calls itself
			const selfCallPattern = new RegExp(`\\b${escapedName}\\s*\\(`, 'g')

			if (selfCallPattern.test(functionBody)) {
				const position = this._getPositionFromIndex(code, match.index)

				markers.push(createMarker(
					MarkerSeverity.Info,
					`Recursive function "${functionName}" detected. Maximum recursion depth is ${JINT_LIMITS.maxRecursionDepth} levels.`,
					position.line,
					position.column,
					position.line,
					position.column + match[0].length
				))
			}
		}
	}

	/**
	 * Check for string concatenation inside loops (performance issue)
	 * @private
	 */
	_checkStringConcatInLoops(code, markers) {
		// Look for += inside for/while blocks
		const loopPattern = /\b(for|while)\s*\([^)]*\)\s*\{/g
		let match

		while ((match = loopPattern.exec(code)) !== null) {
			const loopStart = match.index + match[0].length

			// Find matching closing brace (simplified)
			let braceCount = 1
			let searchIndex = loopStart

			while (braceCount > 0 && searchIndex < code.length) {
				const char = code[searchIndex]

				if (char === '{') braceCount++
				if (char === '}') braceCount--

				searchIndex++
			}

			const loopBody = code.substring(loopStart, searchIndex - 1)

			// Check for string concatenation
			if (/\w+\s*\+=\s*["'`]/.test(loopBody) || /\w+\s*\+=\s*\w+/.test(loopBody)) {
				const position = this._getPositionFromIndex(code, match.index)

				markers.push(createMarker(
					MarkerSeverity.Info,
					'String concatenation in loop detected. Consider using array.push() and array.join("") for better performance.',
					position.line,
					position.column,
					position.line,
					position.column + match[0].length
				))
			}
		}
	}

	/**
	 * Find all matches of a pattern in code with line/column info
	 * @private
	 */
	_findAllMatches(code, pattern) {
		const results = []

		// Always create a fresh pattern with guaranteed 'g' flag to avoid state issues
		const globalPattern = new RegExp(pattern.source, 'g')

		let match
		let iterations = 0
		const MAX_ITERATIONS = 10000 // Safety limit to prevent infinite loops

		while ((match = globalPattern.exec(code)) !== null) {
			// Safety check to prevent runaway loops
			if (++iterations > MAX_ITERATIONS) {
				console.warn('Validator: Pattern matching exceeded iteration limit')
				break
			}

			const position = this._getPositionFromIndex(code, match.index)

			results.push({
				text: match[0],
				line: position.line,
				column: position.column,
				length: match[0].length,
				index: match.index
			})

			// Prevent infinite loops for zero-width matches
			if (match[0].length === 0) {
				globalPattern.lastIndex++
			}
		}

		return results
	}

	/**
	 * Convert character index to line/column position
	 * @private
	 */
	_getPositionFromIndex(code, index) {
		const beforeIndex = code.substring(0, index)
		const lines = beforeIndex.split('\n')

		return {
			line: lines.length,
			column: lines[lines.length - 1].length + 1
		}
	}

	/**
	 * Check if an index is inside a string literal or comment
	 * This prevents false positives for patterns inside strings/comments
	 * @private
	 */
	_isInsideStringOrComment(code, index) {
		let inSingleQuote = false
		let inDoubleQuote = false
		let inTemplateLiteral = false
		let inSingleLineComment = false
		let inMultiLineComment = false

		for (let i = 0; i < index; i++) {
			const char = code[i]
			const prevChar = i > 0 ? code[i - 1] : ''
			const nextChar = i < code.length - 1 ? code[i + 1] : ''

			// Handle escape sequences
			if (prevChar === '\\' && (inSingleQuote || inDoubleQuote || inTemplateLiteral)) {
				continue
			}

			// Handle newlines (end single-line comments)
			if (char === '\n') {
				inSingleLineComment = false
				continue
			}

			// Handle multi-line comment end
			if (inMultiLineComment && char === '*' && nextChar === '/') {
				inMultiLineComment = false
				continue
			}

			// Skip if inside a comment
			if (inSingleLineComment || inMultiLineComment) {
				continue
			}

			// Handle comment starts
			if (!inSingleQuote && !inDoubleQuote && !inTemplateLiteral) {
				if (char === '/' && nextChar === '/') {
					inSingleLineComment = true
					continue
				}

				if (char === '/' && nextChar === '*') {
					inMultiLineComment = true
					continue
				}
			}

			// Handle string delimiters
			if (!inDoubleQuote && !inTemplateLiteral && char === '\'') {
				inSingleQuote = !inSingleQuote
			} else if (!inSingleQuote && !inTemplateLiteral && char === '"') {
				inDoubleQuote = !inDoubleQuote
			} else if (!inSingleQuote && !inDoubleQuote && char === '`') {
				inTemplateLiteral = !inTemplateLiteral
			}
		}

		return inSingleQuote || inDoubleQuote || inTemplateLiteral || inSingleLineComment || inMultiLineComment
	}
}

// Export singleton instance for convenience
export const jintValidator = new JintValidator()
